/**
 * Footage lighting — the CSS filter for `RenderLayout.video`.
 *
 * `dim` washes black OVER the footage (a separate layer); `brightness` and
 * `contrast` light the footage ITSELF, as a filter on the video element.
 *
 * THE INVARIANT (read before touching this): absent or identity (1) must emit
 * NO filter. `clipEditState.baselineLayout` materializes `brightness: 1,
 * contrast: 1` into every layout it rebuilds — which is every layout an
 * operator has ever dragged. The moment identity produced a filter string,
 * every already-edited clip would start re-rendering through a filter pass it
 * never had, and "no lighting edit" would stop being pixel-free.
 *
 * Non-finite values (a corrupt patch, a NaN out of a slider) are dropped
 * rather than serialized: `filter: brightness(NaN)` is an invalid declaration
 * Chrome discards silently, which would look like the knob simply not working.
 */

/** The footage-treatment half of a layout that this module reads. */
type Lighting = { brightness?: number; contrast?: number };

/** `brightness(b) contrast(c)`, omitting every absent / identity / non-finite
 *  part; `undefined` when nothing is left to say. */
export function videoFilterCss(v?: Lighting | null): string | undefined {
  const parts: string[] = [];
  if (v) {
    if (typeof v.brightness === "number" && Number.isFinite(v.brightness) && v.brightness !== 1) {
      parts.push(`brightness(${v.brightness})`);
    }
    if (typeof v.contrast === "number" && Number.isFinite(v.contrast) && v.contrast !== 1) {
      parts.push(`contrast(${v.contrast})`);
    }
  }
  return parts.length > 0 ? parts.join(" ") : undefined;
}

/**
 * The same decision as a spreadable prop bag: `{...videoFilterStyle(video)}`
 * on a footage element adds a `style` prop ONLY when there is a filter to
 * apply. Spreading `{}` leaves the element byte-identical to the one the
 * composition built before this module existed — which is why the compositions
 * spread this rather than passing `style={{ filter }}` with a possibly-
 * undefined value (that would still create a style object).
 */
export function videoFilterStyle(v?: Lighting | null): { style?: { filter: string } } {
  const filter = videoFilterCss(v);
  return filter ? { style: { filter } } : {};
}
